import type {
  StockClipForReview,
  StockClipTagStatus,
} from '@borradh-workspace/api-client/types';
import {
  bodyRegionLabels,
  tagDisagreementLabels,
} from '@borradh-workspace/api-client/types';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ImageIcon,
  Sparkles,
  Type,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TreatmentSelectionFields } from '@/features/services-dashboard/service-footage-fields';
import { cn } from '@/lib/utils';
import type { TreatmentCatalogueEntry } from '@borradh-workspace/labels';

import { StockClipPlayer, trimWindowLabel } from './stock-clip-player';
import {
  type StockClipTags,
  type Technique,
  isBodyRegionValue,
  tagsFromClip,
} from './stock-clip-tags-editor';

/** Widgets that already use the arrow keys to move between their items. */
const ARROW_KEY_WIDGETS =
  '[role="tablist"], [role="group"], [role="radiogroup"], [role="toolbar"], [role="menu"], [role="listbox"], [role="slider"]';

export type StockClipDecision = Exclude<StockClipTagStatus, 'pending'>;

interface StockClipReviewPanelProps {
  clip: StockClipForReview;
  techniques: Technique[];
  catalogue?: TreatmentCatalogueEntry[];
  isSaving: boolean;
  onDecide: (decision: StockClipDecision, tags: StockClipTags) => void;
  /** Turns on C / R / arrow-key shortcuts, for the one-at-a-time queue. */
  shortcuts?: { onPrevious?: () => void; onNext?: () => void };
  /** Shown above the clip, e.g. the queue position and navigation. */
  header?: ReactNode;
  autoPlay?: boolean;
}

interface Guess {
  techniqueSlug: string | null;
  /** The product type guessed, when the treatment has several. */
  agentSlug?: string | null;
  regions: string[];
  /** Picture guess only: the clip shows no specific treatment. */
  isGeneric?: boolean | null;
}

/**
 * One stock clip, reviewed: the clip on the left, and on the right what the AI
 * thinks in plain words, the two independent guesses with a one-click "use",
 * the tags to confirm, and the decision. The AI only ever suggests.
 */
export function StockClipReviewPanel({
  clip,
  techniques,
  catalogue = [],
  isSaving,
  onDecide,
  shortcuts,
  header,
  autoPlay = false,
}: StockClipReviewPanelProps) {
  const [tags, setTags] = useState<StockClipTags>(() => tagsFromClip(clip));
  const review = clip.tagReview;
  const idPrefix = `stock-${clip.id}`;

  const techniqueName = (slug: string | null) =>
    slug
      ? (techniques.find((t) => t.slug === slug)?.displayName ?? slug)
      : 'No treatment';
  const productName = (techniqueSlug: string | null, agent?: string | null) =>
    agent
      ? (techniques
          .find((t) => t.slug === techniqueSlug)
          ?.agents?.find((a) => a.slug === agent)?.displayName ?? null)
      : null;
  const describe = (g: Guess) => {
    const areas = g.regions
      .filter(isBodyRegionValue)
      .map((r) => bodyRegionLabels[r]);
    const product = productName(g.techniqueSlug, g.agentSlug);
    const what =
      !g.techniqueSlug && g.isGeneric
        ? 'A generic clip, no treatment'
        : `${techniqueName(g.techniqueSlug)}${product ? ` (${product})` : ''}`;
    return `${what}${areas.length ? ` on ${areas.join(', ')}` : ''}`;
  };
  const guessChoice = (g: Guess) =>
    catalogue.find((c) => g.agentSlug && c.agentSlug === g.agentSlug) ??
    catalogue.find((c) => c.slug === g.techniqueSlug);
  const inUse = (g: Guess) =>
    tags.treatmentScopes.some((s) => s.treatmentSlug === guessChoice(g)?.slug);
  const use = (g: Guess) => {
    const choice = guessChoice(g);
    const regions = g.regions.filter(isBodyRegionValue);
    setTags((t) => ({
      ...t,
      treatmentScopes: choice
        ? [
            {
              treatmentSlug: choice.slug,
              areaStatus: regions.length ? 'selected' : 'unknown',
              regions,
              procedureNeutral: false,
            },
          ]
        : [],
    }));
  };

  const missingDescription = !tags.visualDescription.trim();
  const missingTreatment =
    !tags.isGeneric &&
    (!tags.treatmentScopes.length ||
      tags.treatmentScopes.some(
        (s) =>
          catalogue.find((t) => t.slug === s.treatmentSlug)?.kind ===
            'family' &&
          !s.procedureNeutral &&
          !s.supportingStep
      ));
  const blocker = missingDescription
    ? 'Add a description before confirming.'
    : missingTreatment
      ? 'Choose a catalogue treatment and review its scope, or mark the clip as generic.'
      : null;

  const confirm = () => {
    if (!isSaving && !blocker) onDecide('confirmed', tags);
  };
  const reject = () => {
    if (!isSaving && !missingDescription) onDecide('rejected', tags);
  };

  // Keyboard shortcuts read the latest state through a ref so the listener is
  // bound once per clip, not on every keystroke.
  const latest = useRef({ confirm, reject, shortcuts });
  latest.current = { confirm, reject, shortcuts };
  const shortcutsOn = Boolean(shortcuts);
  useEffect(() => {
    if (!shortcutsOn) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [cmdk-root], [role="dialog"]'
        ) ||
        document.querySelector('[data-radix-popper-content-wrapper]')
      ) {
        return;
      }
      const { confirm, reject, shortcuts } = latest.current;
      const key = e.key.toLowerCase();
      const isArrow = e.key === 'ArrowRight' || e.key === 'ArrowLeft';
      // Tabs and chip groups move their own focus with the arrow keys; an
      // arrow pressed there must not also change the clip.
      if (isArrow && target?.closest(ARROW_KEY_WIDGETS)) return;
      if (key === 'c') confirm();
      else if (key === 'r') reject();
      else if (e.key === 'ArrowRight') shortcuts?.onNext?.();
      else if (e.key === 'ArrowLeft') shortcuts?.onPrevious?.();
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcutsOn]);

  return (
    <div className="space-y-4">
      {header}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        {/* The clip stays in view while the tags column scrolls. */}
        <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          <StockClipPlayer clip={clip} autoPlay={autoPlay} />
          <div className="space-y-1">
            <p className="font-medium">
              {clip.sourceTitle ? (
                <>“{clip.sourceTitle}”</>
              ) : (
                <span className="text-muted-foreground">
                  No title from the stock library
                </span>
              )}
            </p>
            <p className="text-muted-foreground text-sm">
              {trimWindowLabel(clip)}
            </p>
            {clip.reviewedByName && clip.reviewedAt ? (
              <p className="text-muted-foreground text-sm">
                {clip.tagStatus === 'rejected' ? 'Rejected' : 'Confirmed'} by{' '}
                {clip.reviewedByName} on{' '}
                {new Date(clip.reviewedAt).toLocaleDateString()}
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-5">
          <AiVerdict clip={clip} />

          {review ? (
            <div className="divide-y rounded-lg border">
              <GuessRow
                icon={<ImageIcon className="size-4" />}
                label="From the picture"
                text={review.picture ? describe(review.picture) : null}
                missing="The picture could not be read."
                inUse={review.picture ? inUse(review.picture) : false}
                onUse={
                  review.picture
                    ? () => use(review.picture as Guess)
                    : undefined
                }
                disabled={isSaving}
              />
              <GuessRow
                icon={<Type className="size-4" />}
                label="From the title"
                text={review.title ? describe(review.title) : null}
                missing={
                  clip.sourceTitle
                    ? 'The title names no treatment.'
                    : 'No title to read.'
                }
                inUse={review.title ? inUse(review.title) : false}
                onUse={
                  review.title ? () => use(review.title as Guess) : undefined
                }
                disabled={isSaving}
              />
            </div>
          ) : null}

          <TreatmentSelectionFields
            scopes={tags.treatmentScopes}
            catalogue={catalogue}
            clip
            disabled={isSaving}
            idPrefix={idPrefix}
            onChange={(scopes) =>
              setTags((t) => ({
                ...t,
                treatmentScopes: scopes.map((s) => ({
                  ...t.treatmentScopes.find(
                    (old) => old.treatmentSlug === s.treatmentSlug
                  ),
                  treatmentSlug: s.treatmentSlug,
                  areaStatus:
                    s.areaStatus === 'not_applicable'
                      ? 'unknown'
                      : s.areaStatus,
                  regions: s.regions,
                  procedureNeutral: s.procedureNeutral ?? false,
                })),
              }))
            }
          />
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-description`}>What is visible</Label>
            <Textarea
              id={`${idPrefix}-description`}
              value={tags.visualDescription}
              disabled={isSaving}
              onChange={(e) =>
                setTags((t) => ({ ...t, visualDescription: e.target.value }))
              }
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${idPrefix}-generic`}
              checked={tags.isGeneric}
              disabled={isSaving}
              onCheckedChange={(v) =>
                setTags((t) => ({ ...t, isGeneric: v === true }))
              }
            />
            <Label htmlFor={`${idPrefix}-generic`}>
              Environment or generic footage, no procedure
            </Label>
          </div>

          {/* Pinned to the bottom of the screen, so deciding never needs a scroll. */}
          <div className="sticky bottom-0 z-10 -mx-1 space-y-2 border-t bg-card px-1 pt-4 pb-1">
            {blocker ? (
              <p className="text-amber-700 text-sm dark:text-amber-400">
                {blocker}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={reject}
                disabled={isSaving || missingDescription}
              >
                <X />
                Reject clip
                {shortcutsOn ? <Kbd>R</Kbd> : null}
              </Button>
              <Button
                size="lg"
                onClick={confirm}
                disabled={isSaving || Boolean(blocker)}
              >
                <Check />
                {isSaving ? 'Saving…' : 'Confirm'}
                {shortcutsOn ? <Kbd inverted>C</Kbd> : null}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiVerdict({ clip }: { clip: StockClipForReview }) {
  const review = clip.tagReview;
  const extra = [
    review?.picture?.rejectReason
      ? `The AI would reject this clip: ${review.picture.rejectReason}.`
      : null,
    review?.picture?.qualityFlags.length
      ? `Quality: ${review.picture.qualityFlags.join(', ').replace(/_/g, ' ')}.`
      : null,
    review?.error ? `Part of the suggestion failed: ${review.error}` : null,
  ].filter((line): line is string => Boolean(line));

  if (!review) {
    return (
      <Callout
        tone="muted"
        icon={<Sparkles className="size-4" />}
        title="No AI suggestions yet"
      >
        Tag it by hand, or run the suggestion script for this clip.
      </Callout>
    );
  }
  if (review.agree) {
    return (
      <Callout
        tone="good"
        icon={<CheckCircle2 className="size-4" />}
        title="The picture and the title agree"
        extra={extra}
      >
        Check the clip matches, then confirm.
      </Callout>
    );
  }
  return (
    <Callout
      tone="warn"
      icon={<AlertTriangle className="size-4" />}
      title="Take a closer look"
      extra={extra}
    >
      {review.reasons.map((r) => tagDisagreementLabels[r]).join('. ')}.
    </Callout>
  );
}

function Callout({
  tone,
  icon,
  title,
  children,
  extra = [],
}: {
  tone: 'good' | 'warn' | 'muted';
  icon: ReactNode;
  title: string;
  children: ReactNode;
  extra?: string[];
}) {
  return (
    <div
      className={cn(
        'flex gap-3 rounded-lg border p-3 text-sm',
        tone === 'good' &&
          'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100',
        tone === 'warn' &&
          'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
        tone === 'muted' && 'bg-muted/50'
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        <p className="opacity-90">{children}</p>
        {extra.map((line) => (
          <p key={line} className="opacity-90">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

function GuessRow({
  icon,
  label,
  text,
  missing,
  inUse,
  onUse,
  disabled,
}: {
  icon: ReactNode;
  label: string;
  text: string | null;
  missing: string;
  inUse: boolean;
  onUse?: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <span className="text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className={cn('truncate text-sm', !text && 'text-muted-foreground')}>
          {text ?? missing}
        </p>
      </div>
      {text ? (
        inUse ? (
          <span className="flex items-center gap-1 text-muted-foreground text-xs">
            <Check className="size-3.5" />
            In use
          </span>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={onUse}
            disabled={disabled}
          >
            Use
          </Button>
        )
      ) : null}
    </div>
  );
}

function Kbd({
  children,
  inverted,
}: { children: ReactNode; inverted?: boolean }) {
  return (
    <kbd
      className={cn(
        'ml-1 rounded border px-1.5 font-mono text-[10px] leading-4',
        inverted
          ? 'border-primary-foreground/30 text-primary-foreground/80'
          : 'border-border text-muted-foreground'
      )}
    >
      {children}
    </kbd>
  );
}
